import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import {
  GQActiveWatershedEmployeeFieldsFragment,
  GQAdminFeatureFlagDetailsFragment,
} from '@watershed/app-admin/generated/graphql-operations';

interface AdminContextData {
  activeWatershedEmployee: GQActiveWatershedEmployeeFieldsFragment;
  flagsEnabledMap: Map<string, boolean>;
  // There are two possible side drawers in Admin, accessible on all Org pages.
  openSideDrawer: 'inbox' | 'context' | null;
  setOpenSideDrawer: Dispatch<SetStateAction<'inbox' | 'context' | null>>;
}

const AdminContext = React.createContext<AdminContextData>({
  activeWatershedEmployee: {
    id: 'id',
    name: 'name',
    handle: 'handle',
    email: 'email',
    user: {
      id: 'id',
      createdAt: new Date(),
      primaryLoginOrgId: 'orgId',
      accessibleOrgs: [],
    },
    orgPointsOfContact: [],
  },
  flagsEnabledMap: new Map(),
  openSideDrawer: null,
  setOpenSideDrawer: () => {},
});

export function AdminContextProvider({
  activeWatershedEmployee,
  flagsEnabled,
  children,
}: React.PropsWithChildren<{
  activeWatershedEmployee: GQActiveWatershedEmployeeFieldsFragment;
  flagsEnabled: Array<GQAdminFeatureFlagDetailsFragment>;
}>) {
  const [openSideDrawer, setOpenSideDrawer] = useState<
    'inbox' | 'context' | null
  >(null);

  const flagsEnabledMap = new Map(
    flagsEnabled.map(({ name, activatedForUser }) => [name, activatedForUser])
  );

  return (
    <AdminContext.Provider
      value={{
        activeWatershedEmployee,
        openSideDrawer,
        setOpenSideDrawer,
        flagsEnabledMap,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdminContext() {
  return useContext(AdminContext);
}
